'use client';

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { AltitudeControl } from '@/components/dashboard/AltitudeControl';
import type { AltitudeProfileStatus } from '@/hooks/useAltitudeProfile';
import {
  evaluateCeiling,
  formatAltitude,
  type AltitudeProfile,
  type AltitudeUnit,
} from '@/lib/altitude';

/**
 * The altitude a GO-TO will fly to, set while map-pick mode is armed.
 *
 * Deliberately inline and deliberately BEFORE the map click — the mirror of the
 * dock launch card's own reasoning (see AltitudeControl's header): the operator
 * is about to place a target on the map, and throwing a modal over the map to
 * ask about altitude interrupts the task it belongs to. Here the picker is
 * already answered by the time they click, so the click still dispatches
 * immediately and the flow stays one-click.
 *
 * Collapsed by default for the same reason the dock launch card collapses its
 * picker. This strip sits in the map column, so every pixel it occupies is a
 * pixel of map that disappears the moment pick mode arms — and MapLibre
 * re-centres on the smaller container, moving the target the operator was about
 * to click. One line costs almost nothing; the full picker is one press away
 * for the minority of repositions that change altitude at all.
 *
 * It is a sibling of the command bar rather than part of it: the bar is a fixed
 * 60px toolbar whose key positions must not move, because muscle memory is the
 * point of a command surface.
 */
export interface GotoAltitudeBarProps {
  callsign: string;
  profile: AltitudeProfile;
  /** Label for whoever owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  authority: string | null;
  /** Anything but 'ready' means the profile is platform defaults, not this org's. */
  profileStatus?: AltitudeProfileStatus;
  valueMeters: number;
  onChange: (meters: number) => void;
  unit: AltitudeUnit;
  onUnitChange: (unit: AltitudeUnit) => void;
  /** Disarms pick mode without sending anything. */
  onCancel: () => void;
}

const PANEL_ID = 'cop-goto-altitude-panel';

export function GotoAltitudeBar({
  callsign,
  profile,
  authority,
  profileStatus = 'ready',
  valueMeters,
  onChange,
  unit,
  onUnitChange,
  onCancel,
}: GotoAltitudeBarProps) {
  const [expanded, setExpanded] = useState(false);

  // AltitudeControl renders the ceiling only as an amber track and a 1px marker
  // whose label is aria-hidden, so without this the over-ceiling state would be
  // carried by colour alone — and this is the one altitude surface that
  // dispatches on a single map click. Advisory, exactly as everywhere else.
  const ceiling = evaluateCeiling(valueMeters, { ...profile, unit }, authority);
  const overCeiling = ceiling.status !== 'ok';

  return (
    <div
      role="group"
      aria-label={`Go-to altitude for ${callsign}`}
      className="rounded-lg border border-status-blue/40 bg-card px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* Announced on arm, so a screen-reader operator learns pick mode is
            live and at what altitude without hunting for the panel. */}
        <span
          role="status"
          aria-live="polite"
          className="text-[10px] uppercase tracking-[0.1em] text-status-blue-fg"
        >
          ◎ Go-to armed · {callsign} · {formatAltitude(valueMeters, unit)} AGL
        </span>
        <span className="truncate text-[10px] tracking-[0.04em] text-muted-foreground">
          Click the map to send
        </span>

        {overCeiling && (
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-status-amber-fg">
            <AlertTriangle className="size-3 shrink-0" strokeWidth={1.5} />
            Above ceiling
          </span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={PANEL_ID}
            onClick={() => setExpanded((open) => !open)}
            className="min-h-7 rounded border border-subtle bg-muted/40 px-2.5 py-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Alt {expanded ? '▴' : '▾'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="min-h-7 rounded border border-subtle bg-muted/40 px-2.5 py-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Cancel
          </button>
        </div>
      </div>

      {expanded && (
        <div id={PANEL_ID} className="mt-2">
          {/* The datum is the home point, which for a hand launch is the launch
              point — the bridge sends MAV_CMD_DO_REPOSITION under
              MAV_FRAME_GLOBAL_RELATIVE_ALT_INT. Worded the same as the takeoff
              dialog on purpose: it is the same datum, and two phrasings for one
              datum is a question an operator should never have to stop and ask.
              showRth is off — a reposition sets no return altitude. */}
          <AltitudeControl
            profile={profile}
            authority={authority}
            valueMeters={valueMeters}
            onChange={onChange}
            unit={unit}
            onUnitChange={onUnitChange}
            showRth={false}
            reference="AGL from launch point"
            label="Go-to altitude"
          />

          {ceiling.message && (
            <p
              role="status"
              aria-live="polite"
              className="mt-2 flex items-start gap-1.5 rounded-md border border-status-amber/40 bg-status-amber/10 p-2 text-[10px] leading-relaxed text-status-amber-fg"
            >
              <AlertTriangle className="mt-px size-3 shrink-0" strokeWidth={1.5} />
              <span>{ceiling.message}</span>
            </p>
          )}

          {profileStatus !== 'ready' && (
            <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
              {profileStatus === 'loading'
                ? 'Loading organization altitude profile — values above are platform defaults.'
                : 'Org altitude configuration unavailable — showing platform defaults.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
